<script setup lang="ts">
import SpecialProjectFeedItem from './SpecialProjectFeedItem.vue';
import type { ISpecialProjectFeedSettingsClientDTO } from '~/shared/api/client/special-project-feed/special-project-feed.dto';
import { resizeCropSlot } from '~/shared/lib/resizeImage';
import { CROP_RATIO_FEED_SPECIAL } from '~/shared/lib/crop-ratio.const';
import {
  SPECIAL_PROJECT_FEED_HEADER_DESKTOP_WIDTH,
  SPECIAL_PROJECT_FEED_HEADER_MOBILE_WIDTH,
} from '../lib/const';

const props = defineProps<{
  data: ISpecialProjectFeedSettingsClientDTO;
}>();

// Block settings live in a service publisher_news item with type_item === 'settings'
const settingsItem = computed(() =>
  (props.data.publisher_news || []).find(
    (item) => item.type_item === 'settings',
  ),
);

const items = computed(() =>
  (props.data.publisher_news || []).filter((item) => Boolean(item.news)),
);

const rootStyle = computed(() => ({
  backgroundColor: settingsItem.value?.background_color || undefined,
  '--special-feed-divider-color':
    settingsItem.value?.divider_color || undefined,
}));

const listStyle = computed(() => ({
  borderColor: settingsItem.value?.border_color || undefined,
}));

// 300x70 — desktop feed column, 768x184 — mobile (full-width below 1024px)
const headerSmall = computed(() => settingsItem.value?.image_one?.file_url);
const headerWide = computed(() => settingsItem.value?.image_two?.file_url);

const headerSmallAttrs = computed(() =>
  resizeCropSlot(headerSmall.value, CROP_RATIO_FEED_SPECIAL, 0, {
    width: SPECIAL_PROJECT_FEED_HEADER_DESKTOP_WIDTH,
  }),
);

const headerWideAttrs = computed(() =>
  resizeCropSlot(headerWide.value, CROP_RATIO_FEED_SPECIAL, 1, {
    width: SPECIAL_PROJECT_FEED_HEADER_MOBILE_WIDTH,
  }),
);
</script>

<template>
  <div v-if="items.length > 0" :class="$style.root" :style="rootStyle">
    <picture v-if="headerSmall || headerWide">
      <source
        v-if="headerWide"
        media="(max-width: 1023px)"
        :srcset="headerWideAttrs.srcset || headerWideAttrs.src"
      />
      <img
        :class="$style.header"
        :src="headerSmall ? headerSmallAttrs.src : headerWideAttrs.src"
        :srcset="
          (headerSmall ? headerSmallAttrs.srcset : headerWideAttrs.srcset) ||
          undefined
        "
        :alt="settingsItem?.tag?.name || 'Спецпроект'"
      />
    </picture>

    <div :class="$style.list" :style="listStyle">
      <SpecialProjectFeedItem
        v-for="item in items"
        :key="item.id"
        v-bind="item"
      />
    </div>
  </div>
</template>

<style lang="scss" module>
.root {
  display: flex;
  flex-flow: column nowrap;
  border-bottom: 1px solid var(--color-gray-line);
  color: var(--color-black);
}

.header {
  display: block;
  width: 100%;
  height: auto;
}

@media (min-width: 1024px) {
  .header {
    height: 70px;
    object-fit: cover;
  }
}

.list {
  display: flex;
  flex-flow: column nowrap;
  border: 2px solid transparent;
}
</style>
